import { ChangeEvent, useCallback } from "react";
import { IconButton, styled } from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import { normalizeFileName } from "../utils/utils";

import DynamicIcon from "./dynamic_icon";
import PrimaryButton from "./primary_button";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * DefaultFormDownloadInput
 */

type Props<T extends {}> = {
  name: keyof T;
  nameLimparFlag: keyof T;
  label?: string;
  saving: boolean;
  filename: string | null;
  readOnly?: boolean;
  maxFilenameSize?: number;
  reactHookForm: UseFormReturn<T>;
  isDeleteFileEnabled?: boolean;
  onDeleteFile: () => void;
  onDownloadFile?: (filename: string) => void;
};

export const DefaultFormDownloadInput = <T extends {},>({
  name,
  nameLimparFlag,
  label,
  saving,
  filename,
  readOnly,
  maxFilenameSize = 40,
  reactHookForm,
  isDeleteFileEnabled = true,
  onDeleteFile,
  onDownloadFile,
}: Props<T>) => {
  const handleDownloadFile = useCallback(() => {
    if (!saving) {
      if (filename && filename.length > 0) {
        onDownloadFile?.(filename);
      }
    }
  }, [
    saving,
    filename,
    onDownloadFile,
  ]);

  const handleDeleteFile = useCallback(() => {
    if (!saving && !readOnly) {
      if (window.confirm('Você deseja excluir este item?')) {
        if (filename && filename.length > 0) {
          onDeleteFile?.();
        }
        reactHookForm.setValue(nameLimparFlag as any, true as any);
        reactHookForm.resetField(name as any);
      }
    }
  }, [
    nameLimparFlag,
    name,
    saving,
    readOnly,
    reactHookForm,
    filename,
    onDeleteFile,
  ]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving && !readOnly && e.target.files) {
      const f = e.target.files.item(0);
      if (f) {
        reactHookForm.setValue(nameLimparFlag as any, false as any);
        reactHookForm.setValue(name as any, f as any);
        onChange(f);
      }
    }
  }, [
    name,
    nameLimparFlag,
    saving,
    readOnly,
    reactHookForm,
  ]);

  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field }) => (filename && filename.length > 0) ? (
        <div className="w-full flex flex-row justify-between items-center">
          <span
            className="cursor-pointer text-black dark:text-white text-base"
            onMouseDown={handleDownloadFile}>
            {filename && filename && filename.length > maxFilenameSize
              ? `${filename.substring(0, maxFilenameSize)}...${filename.substring(maxFilenameSize)}`
              : filename}
          </span>
          {isDeleteFileEnabled && (
            <IconButton onClick={handleDeleteFile}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={20} className="text-red-500" />
            </IconButton>
          )}
        </div>
      ) : (
        <PrimaryButton
          component="label"
          role={undefined}
          variant="contained"
          tabIndex={-1}
          disabled={saving}
          startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
          {field.value ? normalizeFileName((field.value as any).name, maxFilenameSize) : (label ?? "Escolher arquivo")}
          {
            !readOnly &&
            <VisuallyHiddenInput
              type="file"
              onChange={(e) => handleFileChange(e, field.onChange)}
              multiple={false} />
          }
        </PrimaryButton>
      )} />
  );
}